// Payment — the PerseusPay dealer payment page.
//
// Optimizations live here: parallel API calls, an in-memory dealer-settings
// cache, graceful SignalR degradation, AxiosError handling and progressive
// render. This file was PaymentOptimized.tsx, served behind a LaunchDarkly
// flag; that flag reached 100% rollout and was retired along with the legacy
// page and the routing gate that chose between them.
import InputMask from "react-input-mask";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import CheckIcon from "@mui/icons-material/Check";
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from "@microsoft/signalr";

import { format } from "date-fns";
import jwt_decode from "jwt-decode";
import { formatUtcLocalPattern } from "../../../utils/dateFormat";
import { isEmpty, Callback } from "../../../utils/Util";
import {
  TargetPayApi,
  JWTDecode,
  ValidatePhoneNumber,
  ValidateEmail,
  isValidUSPhoneNo,
  formatUSPhoneNo,
  GetDealerSettings,
  DealerProfile,
} from "../../../utils/UtilTarget";
import {
  resolveDefaultTab,
  shouldShowTabs,
  canShowTab,
  canShowSaveCardTab,
  hrefIsSaveCardAction,
  hrefIsPaymentAction,
  getTabsFromSearch,
} from "./paymentTabs";
import {
  useEffect,
  useState,
  lazy,
  Suspense,
  useMemo,
  useCallback,
} from "react";
import * as React from "react";
import axios from "axios";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { resolveDeclineErrorMessage } from "../../../utils/declineErrorMessage";
import { PaymentScreenHeader } from "../PaymentScreenHeader";
import { toast } from "react-toastify";
import { FormStyleCollection as formStyles } from "../../../styles/form";
import { makeStyles } from "@mui/styles";
import {
  CardType,
  PaymentStatus,
  StartTransactionType,
} from "../../../types/enums";
import { datadogLogs } from "@datadog/browser-logs";
import { LocaleHelper } from "../../../helpers/LocaleHelper";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import { setDealerId } from "../../../features/dealer/dealerSlice";
import PaymentErrorBoundary from "./PaymentErrorBoundary";
import {
  reportPaymentPageReady,
  reportPaymentHealthError,
} from "./paymentHealth";

// Lazy-loaded payment-method components: only ship the tab the user opens.
const LazyPaymentContainer = lazy(() => import("../AdyenPaymentDropIn"));
const ACHPaymentForm = lazy(() => import("../ACH/AdyenACHpayments"));
const AdyenTransactions = lazy(() => import("../AdyenTransactions"));

const LazyFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    style={{ padding: "20px" }}
  >
    <CircularProgress size="1.5rem" />
  </Box>
);

// The Adyen Drop-in is the core payment surface, so it gets the same protection
// as the page: an error boundary + graceful fallback + health reporting. A crash
// or lazy-load failure here is caught, reported as a health signal, and shown
// as a recoverable message instead of white-screening. A refresh or retry is
// the fix.
//
// Named `PaymentContainer` so every existing call site is protected unchanged;
// the raw lazy component is `LazyPaymentContainer`.
// Non-interactive placeholder shown while the payment is still being validated
// (Tier 3): the amount/tabs paint optimistically, but the card form must not.
const VerifyingPayment = () => (
  <Box
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
      gap: "8px",
    }}
  >
    <CircularProgress size="1.5rem" />
    <span>Verifying payment…</span>
  </Box>
);

const PaymentContainer = ({ validated = true, ...props }: any) => {
  // TIER 3 SAFETY: the Adyen Drop-in self-submits (it charges via its own
  // handler, not through the paymentValidated-gated button flows), so it must
  // not be interactive until ValidateAdyenPaymentToken has confirmed the
  // payment is still payable. Rendering it only once validated also prevents
  // the provisional `saveCard` value from flipping (and resetting in-progress
  // card entry) when the authoritative response lands.
  if (!validated) return <VerifyingPayment />;
  return (
    <PaymentErrorBoundary
      onError={(error, info) =>
        reportPaymentHealthError("adyen-dropin", error, {
          componentStack: info.componentStack?.slice(0, 500),
        })
      }
      fallback={
        <Box style={{ padding: "16px", textAlign: "center" }}>
          The payment form couldn&apos;t be loaded. Please refresh the page and
          try again.
        </Box>
      }
    >
      <Suspense fallback={<LazyFallback />}>
        <LazyPaymentContainer {...props} />
      </Suspense>
    </PaymentErrorBoundary>
  );
};

let MaxretryAttempts = 3;
let retryAttempts = 1;
let midSessionRetryAttempts = 0;
const midSessionRetryDelays = [2000, 4000, 6000, 10000, 20000];
const maxMidSessionRetries = midSessionRetryDelays.length;
let intentionalStop = false;
let dollarUSLocale = LocaleHelper.getCurrencyFormatter("US");

let paymentToken = "";
let paymentProcessed = false;
let paymentStatus = "";
let paymentType = "";
let surchargeAmount = "";
let paymentLinkUrl = "";

// Tab-resolution rules live in ./paymentTabs (shared and
// unit-tested). These thin wrappers keep the existing call sites unchanged.
const isSaveCardAction = (): boolean => hrefIsSaveCardAction();

const isPaymentAction = (): boolean => hrefIsPaymentAction();

const getTabsFromUrl = (): string[] => getTabsFromSearch();

// TIER 1: build the payment-form stylesheet ONCE at module scope (MUI's
// intended usage) instead of recreating and re-processing it on every render
// (the old StyleCreator.makeStyles(...)() pattern). Same class output.
const usePaymentStyles = makeStyles(() => formStyles.all as any);

export default function Payment() {
  //const [signalRconnection, setSignalRConnection] = useState<any>(null);
  const [signalRconnection, setSignalRConnection] = useState<{
    connection: HubConnection | null;
    referenceNumber: string | number | null;
  }>({
    connection: null,
    referenceNumber: null,
  });
  const [signalRconnectionNegotiation, setSignalRConnectionNegotiation] =
    useState<boolean>(false);
  const [token, setToken] = React.useState("");
  // TIER 1: decode the JWT once per token instead of re-decoding it on every
  // render (CheckTab / AmountLabel / SaveCard + JSX all read claims repeatedly).
  const tokenClaims = useMemo<any>(() => {
    try {
      return token ? (jwt_decode(token) as any) : {};
    } catch {
      return {};
    }
  }, [token]);
  const [terminals, setTerminals] = React.useState<any>([]);
  const [paymentData, setPaymentData] = React.useState<any>([]);
  const [linkEmail, setLinkEmail] = React.useState<string>("");
  const [linkPhone, setLinkPhone] = React.useState<string>("");
  const [VaultedCards, SetVaultedCards] = React.useState<any>(null);
  const [vaultedCardsLoading, setVaultedCardsLoading] =
    React.useState<boolean>(true);
  // Bug 181752 (02): track the vaulted-card fetch so the Token panel can show a
  // loader instead of a premature "No card saved" while the fetch is in flight.
  const [VaultedCardId, SetVaultedCardId] = React.useState("");
  const [terminal, setTerminal] = useState("0");
  const [isProcessing, setIsProcessing] = React.useState<any>(true);
  const [callbackPosted, setCallbackPosted] = React.useState(false);
  const [isPageLoading, setIsPageLoading] = React.useState<boolean>(true);
  // TIER 3: paint the shell optimistically from token claims (pagePainted),
  // but keep the interactive tabs/panels gated until ValidateAdyenPaymentToken
  // confirms the payment (paymentValidated). Validation stays authoritative:
  // on any anomaly we revert to the default/error state.
  const [paymentValidated, setPaymentValidated] = React.useState(false);
  const [pagePainted, setPagePainted] = React.useState(false);
  const [tabValue, setTabValue] = React.useState(SetDefaultTab());
  const classes: any = usePaymentStyles();
  const [PaymentLinkResponse, SetPaymentLinkResponse] =
    React.useState<any>(null);
  const [paymentDone, setPaymentDone] = React.useState<any>(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [PlatformFeeFlag, SetPlatformFeeFlag] = React.useState<boolean>(false);
  const [manualPaymentStatus, SetManualPaymentStatus] = React.useState<any>("");
  const [EmergepayIsSandBox, setEmergepayIsSandBox] = React.useState(-1);

  const [companyDealerId, setcompanyDealerId] = React.useState("");
  const [isTokenValidated, setIsTokenValidated] = useState(false);
  const dispatch = useDispatch();
  const dealerIdFromState = useAppSelector(
    (state: RootState) => state.dealer.dealerId,
  );
  const dataLogger = datadogLogs.logger;

  const [isACHPaymentEnabled, setisACHPaymentEnabled] = React.useState<boolean>(
    localStorage.getItem("isACHPaymentEnabled") === "true",
  );
  const [moreInfo, setMoreInfo] = React.useState<string>("");

  // Bug Fix : 161757
  // LD Settings
  const flags = useFlags();
  let singleSessionManualTabEnabled = false;
  let singleSessionSaveCardTabEnabled = false;
  let allowVaultedCardCreditReturn = false;

  singleSessionManualTabEnabled = flags.singleSessionManualTabEnabled;
  singleSessionSaveCardTabEnabled = flags.singleSessionSaveCardTabEnabled;
  allowVaultedCardCreditReturn = flags.allowVaultedCardCreditReturn;

  // ManualPayment
  const [manualTabVisited, setManualTabVisited] = useState(false);
  // SaveCard Action
  const [saveCardTabVisited, setSavecardTabVisited] = useState(false);

  // Return type kept as `any` to preserve the original inferred type of this
  // function (its TitleCase branch came from untyped JS), so downstream
  // `tabValue` usage compiles unchanged. The module keeps the precise
  // `string | false` type for tests and this page.
  function SetDefaultTab(): any {
    return resolveDefaultTab(
      isSaveCardAction(),
      getTabsFromUrl(),
      tokenClaims.TransactionType,
    );
  }

  useEffect(() => {
    if (isTokenValidated) {
      let obj: any = new Object();
      obj.Type = "TabChange";
      obj.Tab = tabValue;
      let jsonString = JSON.stringify(obj);
      Callback(jsonString, paymentData.hostUrl);
    }
  }, [tabValue]);

  useEffect(() => {
    // Success beacon: the page mounted and rendered — independent of the
    // eventual payment outcome (a later decline does not undo readiness).
    reportPaymentPageReady();
    window.addEventListener("message", EmergePayResponse);
    paymentToken = "";
    document.title = "New Payment";
    paymentProcessed = false;
    paymentType = "";

    const validate = async () => {
      await ValidateToken();
      setIsTokenValidated(true);
    };
    validate();
  }, []);

  const EmergePayResponse = (event: any) => {
    if (isEmpty(event.data) || isEmpty(event.data.isApproved)) return;

    try {
      let urlParams = new URLSearchParams(window.location.search);
      event.data.isMobile = urlParams.get("ismobile");
      Callback(event.data, paymentData.hostUrl);
    } catch (error) {
      console.error("Error: Payment.EmergePayResponse: " + error);
      dataLogger.error("Error: Payment.EmergePayResponse: " + error);
    }
  };

  function ShowTabs(): any {
    // Bug 181752 (03): the SaveCard route always resolves to a real tab
    // (Savecard / Savecardterminal, including the Savecardterminal fallback for
    // an unrecognized value), so always show the strip and let its header reflect
    // the panel being shown — never render an empty/hidden strip here.
    if (isSaveCardAction()) return true;
    return shouldShowTabs(getTabsFromUrl(), tokenClaims.TransactionType);
  }

  const CheckSaveCardTabs = useCallback((tab: string): boolean => {
    return canShowSaveCardTab(tab, {
      isSaveCard: isSaveCardAction(),
      tabs: getTabsFromUrl(),
    });
  }, []);

  const CheckTab = useCallback(
    (tab: string): any => {
      if (
        tab === "Token" &&
        tokenClaims.TransactionTypeId === "1" &&
        !allowVaultedCardCreditReturn
      ) {
        return false;
      }

      return canShowTab(tab, {
        isSaveCard: isSaveCardAction(),
        tabs: getTabsFromUrl(),
        transactionType: tokenClaims.TransactionType,
      });
    },
    [tokenClaims, allowVaultedCardCreditReturn],
  );

  // tokenClaims is decoded asynchronously (after ValidateToken), so the initial
  // SetDefaultTab() at useState time ran with empty claims and couldn't pick the
  // CreditReturn default. Re-resolve the default tab in the SAME render the claims
  // change (not an effect) so <Tabs value> is never left pointing at a tab that
  // CheckTab has just hidden — an effect would correct it a render too late and
  // MUI would warn about an invalid value in between.
  const [defaultedForTxnType, setDefaultedForTxnType] = useState<
    string | undefined
  >(undefined);
  if (tokenClaims.TransactionType !== defaultedForTxnType) {
    setDefaultedForTxnType(tokenClaims.TransactionType);
    setTabValue(SetDefaultTab());
  }

  async function makeSignalRConnection(referenceNumber: any, isRetry = false) {
    // Only reset the retry counters when INITIATING a fresh connection. A
    // recursive call from onclose (mid-session) or the 401 start-retry passes
    // isRetry=true, so the counters accumulate and the caps are actually
    // reached (previously the unconditional reset here made the mid-session cap
    // unreachable → infinite reconnect loop, and left retryAttempts stuck high
    // across payments in one SPA session).
    if (!isRetry) {
      midSessionRetryAttempts = 0;
      retryAttempts = 1;
    }
    const ReferenceIdForClients = referenceNumber;
    const negotiateRequestBody = { ReferenceIdForClients };

    let apiKey = sessionStorage.getItem("nonce");
    let header;
    if (!isEmpty(apiKey)) {
      header = {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-API-KEY": apiKey,
      };
    } else {
      header = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      };
    }
    const negotiateRequestHeader = {
      headers: header,
    };
    try {
      let url = `${process.env.REACT_APP_BASE_URL}Notification/Negotiate`;
      let negotiateResponse: any = await axios.post(
        url,
        negotiateRequestBody,
        negotiateRequestHeader,
      );
      let connection: any = new HubConnectionBuilder()
        .withUrl(negotiateResponse.data.clientConnectionUrl, {
          accessTokenFactory: () => negotiateResponse.data.accessToken,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .build();

      // Mid-session reconnect: when auto-reconnect exhausts all retries
      connection.onclose(async (closeError: any) => {
        if (intentionalStop) {
          console.log("[SignalR] Connection closed (intentional stop).");
          intentionalStop = false;
          return;
        }

        if (closeError) {
          console.error(
            "[SignalR] Connection closed with error:",
            closeError.message,
          );
          dataLogger.error(
            `SignalR connection closed with error: ${closeError.message} for paymentId: ${paymentData.paymentId}`,
          );
        } else {
          console.warn(
            "[SignalR] Connection closed. Auto-reconnect exhausted.",
          );
          dataLogger.warn(
            `SignalR auto-reconnect exhausted for paymentId: ${paymentData.paymentId}`,
          );
        }

        // Attempt full re-negotiate + reconnect
        midSessionRetryAttempts++;
        if (midSessionRetryAttempts <= maxMidSessionRetries) {
          const delay = midSessionRetryDelays[midSessionRetryAttempts - 1];
          console.warn(
            `[SignalR] Mid-session reconnect attempt ${midSessionRetryAttempts} of ${maxMidSessionRetries} (retrying in ${delay}ms)...`,
          );
          dataLogger.warn(
            `SignalR mid-session reconnect attempt ${midSessionRetryAttempts} of ${maxMidSessionRetries} for paymentId: ${paymentData.paymentId}`,
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          await makeSignalRConnection(referenceNumber, true);
        } else {
          console.error(
            `[SignalR] All ${maxMidSessionRetries} mid-session reconnect attempts exhausted. Giving up.`,
          );
          // Graceful degradation (§7): inform without freezing the payment UI.
          reportPaymentHealthError("signalr-reconnect", closeError, {
            paymentId: paymentData.paymentId,
          });
          toast.info(
            "Live payment status updates were interrupted. Your payment can still be completed — please refresh the page if the status doesn't update automatically.",
            {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 6000,
              closeOnClick: true,
              toastId: "signalr-degraded",
            },
          );
          dataLogger.error(
            `SignalR mid-session reconnection failed after ${maxMidSessionRetries} attempts for paymentId: ${paymentData.paymentId}`,
          );
        }
      });

      connection.onreconnecting((error: any) => {
        console.warn(
          "[SignalR] Connection lost. Auto-reconnecting with retries [0, 2s, 5s, 10s, 30s]...",
          { error: error?.message },
        );
        dataLogger.warn(
          `SignalR reconnecting for paymentId: ${paymentData.paymentId}, error: ${error?.message}`,
        );
      });

      connection.onreconnected((connectionId: string) => {
        midSessionRetryAttempts = 0;
        console.log("[SignalR] Reconnected.", { connectionId });
        dataLogger.info(
          `SignalR reconnected with connectionId: ${connectionId} for paymentId: ${paymentData.paymentId}`,
        );
      });

      setSignalRConnection({ connection, referenceNumber });
    } catch (error) {
      console.error("Error: SignalR negotiation failed: ", error);
      dataLogger.error("Error: SignalR negotiation failed: ", error);
      // Negotiate failed → no connection is created, so start()'s .catch (which
      // normally reports signalr-connect) never runs. Report the health signal
      // here so the Guarded Release guardrail still sees the failure.
      reportPaymentHealthError("signalr-negotiate", error, {
        paymentId: paymentData.paymentId,
      });
    }
  }
  useEffect(() => {
    if (signalRconnection.connection) {
      if (signalRconnection.connection.state !== HubConnectionState.Connected) {
        console.log(
          "SignalR connectionState.0: " + signalRconnection.connection.state,
        );

        signalRconnection.connection
          .start()
          .then(() => {
            // Connection established — reset retry counters so a later drop or
            // a future connection starts with a full budget.
            midSessionRetryAttempts = 0;
            retryAttempts = 1;
            setSignalRConnectionNegotiation(true);
            console.log(
              "SignalR connectionState.1: " +
                signalRconnection.connection.state,
            );
            signalRconnection.connection.on("newMessage", (message) => {
              CheckPaymentResponse(message.payload);
            });
          })
          .catch(async (error) => {
            console.error("SignalR start error:", error);
            dataLogger.error(
              `SignalR start error for paymentId: ${paymentData.paymentId}: ${error}`,
            );

            // Graceful degradation (§7): retry a 401 (expired negotiate token)
            // without disturbing the payment UI. Only when retries are
            // exhausted (or the error is not recoverable) do we surface a
            // NON-blocking notice — never an autoClose:false toast that freezes
            // the page, and never a toast while a retry is still in flight.
            const is401 =
              error?.message?.includes("401") ||
              error?.toString().includes("401");

            if (is401 && retryAttempts < MaxretryAttempts) {
              retryAttempts++;
              console.log(
                `Retrying SignalR negotiation due to 401 (attempt ${retryAttempts}/${MaxretryAttempts})...`,
              );
              await makeSignalRConnection(
                signalRconnection.referenceNumber,
                true,
              );
              return;
            }

            // Retries exhausted or non-recoverable error: degrade cleanly. The
            // payment can still be completed; only live status updates are lost.
            setSignalRConnectionNegotiation(false);
            dataLogger.error(
              `SignalR connection could not be established for paymentId: ${paymentData.paymentId} after ${retryAttempts} attempt(s): ${error}`,
            );
            reportPaymentHealthError("signalr-connect", error, {
              paymentId: paymentData.paymentId,
            });
            toast.info(
              "Live payment status updates are temporarily unavailable. Your payment can still be completed — please refresh the page if the status doesn't update automatically.",
              {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 6000,
                closeOnClick: true,
                toastId: "signalr-degraded",
              },
            );
          });
      }
    }
  }, [signalRconnection.connection]);

  function CheckPaymentResponse(paymentContent: any) {
    dataLogger.info(
      `CheckPaymentResponse method called with payload: ${paymentContent} for the paymentId: ${paymentData.paymentId}`,
    );

    try {
      const payment = JSON.parse(paymentContent);
      if (payment.notificationItems.length > 0) {
        // Ignore $0 ACH authorizations
        if (
          payment.notificationItems[0].NotificationRequestItem.paymentMethod ==
            "ach" &&
          payment.notificationItems[0].NotificationRequestItem.amount.value == 0
        ) {
          console.log("Ignoring $0 ACH authorization.");
          return;
        }

        paymentData.status =
          payment.notificationItems[0].NotificationRequestItem.success ===
          "true"
            ? "Success"
            : "Failed";

        // Adyen's webhook "reason" field is not guaranteed to be a failure message when the
        // payment succeeded (it can carry unrelated card-reference data on an approved
        // authorisation), so only ever treat it as a decline reason when the status is Failed.
        const declineReason =
          payment.notificationItems[0].NotificationRequestItem.reason;
        const errorMessageReason =
          paymentData.status === "Failed" ? declineReason : "";

        CallBackPaymentResponse(
          paymentData.status,
          "Payment",
          declineReason,
          payment,
          "",
          "",
          "",
          0,
          "",
          resolveDeclineErrorMessage(errorMessageReason, paymentType),
        );
      }
    } catch (error) {
      alert("CheckPaymentResponse.Error: " + error);
      dataLogger.error(
        `CheckPaymentResponse failed with Error : ${error} for the paymentId: ${paymentData.paymentId}`,
      );
    }
  }

  const Terminals = useCallback(() => {
    try {
      if (terminals == null || terminals.data == null) return [];
      return terminals.data;
    } catch (error) {
      return [];
    }
  }, [terminals]);

  async function ValidateToken() {
    var urlParams = new URLSearchParams(window.location.search);
    let access_token = urlParams.get("access_token");
    let refresh_token = urlParams.get("refresh_token");
    if (!isEmpty(access_token))
      localStorage.setItem("access_token", access_token);
    if (!isEmpty(refresh_token))
      localStorage.setItem("refresh_token", refresh_token);

    let token = urlParams.get("payment_token");
    if (isEmpty(token)) token = urlParams.get("token");
    if (isEmpty(token) || token.length < 5) {
      setErrorMessage("Invalid/expired token");
      setIsProcessing(false);
      return;
    }

    paymentToken = token;
    setToken(token);
    setIsProcessing(true);
    setCallbackPosted(false);
    if (token.length > 50) {
      let nonce = JWTDecode(token, "Nonce");
      if (!isEmpty(nonce)) {
        nonce = nonce.split("").reverse().join("");
        sessionStorage.setItem("nonce", nonce);
      }

      // --- OPTIMIZATION (§7): parallelize the independent calls ---
      // DealerId and DealerInfo are non-sensitive claims already present on the
      // payment_token (PerseusPayService.CreateClaimsFromPaymentRequest), so the
      // dealer-scoped fetches can start immediately instead of waiting for the
      // (slow, avg ~5s) ValidateAdyenPaymentToken round-trip. That call remains
      // the authoritative gate for payment status/route validation below.
      // Decode the token's claims once locally (component-level tokenClaims
      // isn't updated until the next render).
      let claims: any = {};
      try {
        claims = jwt_decode(token);
      } catch {
        claims = {};
      }
      const tokenDealerId = Number(claims.DealerId);
      const hasTokenDealerId =
        Number.isFinite(tokenDealerId) && tokenDealerId > 0;
      const tokenDealerInfo = claims.DealerInfo;

      // --- TIER 3: optimistic paint from non-sensitive token claims ---
      // Render the amount/header immediately so the page isn't blank while the
      // (slow) ValidateAdyenPaymentToken call runs. This is PROVISIONAL — the
      // validation response below stays authoritative; on any anomaly (expired /
      // already-processed / invalid route) we clear this and show the default
      // error state, and no submit is possible until paymentValidated is true.
      if (hasTokenDealerId) {
        const reqAmt = Number(claims.RequestedAmount) || 0;
        const pFee = Number(claims.PlatformFee) || 0;
        const tax = Number(claims.SalesTaxAmount) || 0;
        setPaymentData({
          invoiceId: claims.InvoiceId ?? "",
          requestedAmount: reqAmt,
          platformFee: pFee,
          salesTaxAmount: tax,
          totalAmount: reqAmt + pFee + tax,
          currency: claims.Currency ?? "",
          customerName: claims.CustomerName ?? "",
          customerId: claims.CustomerId ?? "",
          customerEmail: claims.CustomerEmail ?? "",
          customerPhone: claims.CustomerPhone ?? "",
          transactionType: claims.TransactionType ?? "",
          transactionTypeId: Number(claims.TransactionTypeId) || 0,
          saveCard: String(claims.SaveCard).toLowerCase() === "true",
          POIID: claims.POIID ?? "",
          dealerInfo: tokenDealerInfo ?? "",
          paymentId: claims.PaymentId ?? "",
          __provisional: true,
        });
        setPagePainted(true);
      }

      const validatePromise = TargetPayApi("ValidateAdyenPaymentToken", token);

      // Dealer settings — fetched fresh in parallel with validation, keyed to
      // the token's DealerId. Intentionally NOT cached client-side:
      // GetDealerSettings writes GLOBAL (non-dealer-scoped) localStorage keys,
      // so serving a cache "hit" could show another dealer's merchant/fee/ACH
      // config after a dealer switch. One fetch per load is correct and cheap.
      let dealerSettingsPromise: Promise<any> = hasTokenDealerId
        ? GetDealerSettings(tokenDealerId).catch((e: any) =>
            dataLogger.error(
              `GetDealerSettings failed for dealerId: ${tokenDealerId}. Error: ${e}`,
            ),
          )
        : Promise.resolve();

      // Terminals — started early from the token's DealerId/DealerInfo. Wrapped
      // in .catch so the awaited result (below) still owns error handling.
      let storeTerminalsPromise: Promise<any> | null =
        hasTokenDealerId && !isEmpty(tokenDealerInfo)
          ? TargetPayApi(
              "StoreTerminals",
              { dealerId: tokenDealerId },
              "get",
              "PerseusPay",
              true,
            ).catch((e: any) => ({ __storeTerminalsError: e }))
          : null;

      const res = await validatePromise;

      if (
        isEmpty(res) ||
        (res.status !== PaymentStatus.Pending &&
          res.status !== "PendingRefund") ||
        !isEmpty(res.pspReference)
      ) {
        setErrorMessage("Invalid/Expired access token.");
        // TIER 3: validation failed — revert any optimistic UI to the default
        // state (clear provisional data, keep submit gated).
        setPaymentData([]);
        setPaymentValidated(false);
        setIsProcessing(false);
        setIsPageLoading(false);
        setSignalRConnectionNegotiation(true);
        return;
      }

      setIsProcessing(false);
      if (!isEmpty(res)) {
        // Validate route based on transaction type
        const validation = isValidRoute(res.transactionTypeId);

        if (!validation.isValid) {
          setErrorMessage(validation.message!);
          // TIER 3: revert optimistic UI to the default state on invalid route.
          setPaymentData([]);
          setPaymentValidated(false);
          setSignalRConnectionNegotiation(true);
          return;
        }

        const company_dealer_id = res.companyDealerId;
        setcompanyDealerId(company_dealer_id);
        if (isEmpty(dealerIdFromState)) {
          // ? not already set in Redux state
          if (!isEmpty(access_token)) {
            // ? URL has access_token param
            const dealerIdFromProfile = await DealerProfile("dealerId"); // ? decode JWT ? parse profile JSON ? get dealerId
            if (!isEmpty(dealerIdFromProfile)) {
              dispatch(setDealerId(dealerIdFromProfile.toString()));
            }
          } else if (!isEmpty(res.dealerId))
            dispatch(setDealerId(res.dealerId));
          else dispatch(setDealerId(company_dealer_id));
        }
        if (isACHPaymentEnabled) {
          setisACHPaymentEnabled(res.currency === "USD" && res.totalAmount > 0);
        }
        // Fire-and-forget: fetch vaulted cards concurrently — no data
        // dependency on terminals/settings.
        GetCustomerToken(company_dealer_id, res.customerId);

        // Reconcile the authoritative dealerId with the token's. They match in
        // the normal case. Only a valid, POSITIVE res.dealerId that differs
        // counts as a real mismatch — guards against Number(null)===0 and NaN
        // spuriously firing a re-fetch. On a genuine mismatch (grouped-dealer
        // edge, or the token had no usable DealerId), let the early fetch settle
        // first, then re-fetch with the authoritative res.dealerId LAST so its
        // global localStorage writes deterministically win the race.
        const resDealerId = Number(res.dealerId);
        const dealerIdMismatch =
          Number.isFinite(resDealerId) &&
          resDealerId > 0 &&
          resDealerId !== tokenDealerId;
        if (!hasTokenDealerId || dealerIdMismatch) {
          await dealerSettingsPromise.catch(() => {});
          storeTerminalsPromise = !isEmpty(res.dealerInfo)
            ? TargetPayApi(
                "StoreTerminals",
                { dealerId: res.dealerId },
                "get",
                "PerseusPay",
                true,
              ).catch((e: any) => ({ __storeTerminalsError: e }))
            : null;
          dealerSettingsPromise = GetDealerSettings(res.dealerId).catch(
            (e: any) =>
              dataLogger.error(
                `GetDealerSettings failed for dealerId: ${res.dealerId}. Error: ${e}`,
              ),
          );
        }

        await dealerSettingsPromise;
        SetPlatformFeeFlag(
          localStorage.getItem("platformFee_flag_enabled") === "true",
        );
        if (!isEmpty(res.status) && res.status.includes("Pending"))
          res.status = null;
        //TFS : 169095-----------------
        //Evaluate AFTER GetDealerSettings has set the localStorage flag for this dealer
        setisACHPaymentEnabled(
          localStorage.getItem("isACHPaymentEnabled") === "true" &&
            res.currency === "USD" &&
            res.totalAmount > 0,
        );
        //-----------------
        setPaymentData(res);
        // TIER 3: validation confirmed the payment is still payable →
        // replace provisional data with authoritative res and unlock the UI.
        setPaymentValidated(true);
        if (res.void) setTabValue("Payments");
        if (!paymentProcessed)
          paymentProcessed = !isEmpty(res.pspReference) || !isEmpty(res.status);
        if (paymentProcessed) paymentStatus = res.status;
        if (paymentStatus === PaymentStatus.Success) {
          paymentStatus = PaymentStatus.Authorised;
          SetManualPaymentStatus(paymentStatus);
        }
        if (!isEmpty(res.dealerInfo)) {
          try {
            var r: any = await storeTerminalsPromise;
            if (r && r.__storeTerminalsError) {
              throw r.__storeTerminalsError;
            }
            // TargetPayApi(returnError=true) RESOLVES with the AxiosError on
            // failure (it does not reject), so a blocked/network failure arrives
            // here as an Error with no `.response`. Route any returned
            // Error/AxiosError through the catch below so it is reported as a
            // health error instead of being mistaken for an empty success.
            if (r?.isAxiosError || r instanceof Error) {
              throw r;
            }

            if (!isEmpty(r)) {
              const status = r.response?.status;
              const isClientError = status >= 400 && status < 500;
              const isServerError = status >= 500 && status < 600;

              if (isClientError || isServerError) {
                const message = isServerError
                  ? "Unable to load terminals due to a server error. Please try again later or contact support if the issue persists."
                  : "Unable to load terminals. Please refresh the page or contact support if the issue persists.";

                toast.error(message, {
                  position: toast.POSITION.TOP_CENTER,
                  autoClose: 3000,
                  toastId: "store-terminals-error",
                });

                dataLogger.error(
                  `StoreTerminals ${
                    isServerError ? "5xx" : "4xx"
                  } error for DealerId: ${res.dealerId}, PaymentId: ${
                    res.paymentId
                  }. Status: ${status} - ${r.response?.statusText}`,
                );
              } else {
                // Success path � filter and set terminals based on dealerLocationId
                if (r != null && r.data != null) {
                  r.data = r.data.filter(
                    (x: { dealerLocationId: 0 }) =>
                      x.dealerLocationId == 0 ||
                      x.dealerLocationId == res.dealerId,
                  );
                }

                setTerminals(r);
                let POIID = ""; //JWTDecode(token, "POIID");
                if (isEmpty(POIID)) {
                  let DealerInfo = JWTDecode(token, "DealerInfo");
                  const obj = JSON.parse(DealerInfo);
                  if (!isEmpty(obj.UserDefaultPOIID))
                    POIID = obj.UserDefaultPOIID;
                }
                if (isEmpty(POIID)) POIID = JWTDecode(token, "POIID");
                if (!isEmpty(POIID)) {
                  if (r != null) {
                    if (r.data != null) {
                      let item = r.data.find(({ id }) => id === POIID);
                      if (isEmpty(item))
                        item = r.data.find(
                          ({ terminalName }) => terminalName === POIID,
                        );
                      if (!isEmpty(item)) setTerminal(POIID);
                    }
                  }
                }
              }
            }
          } catch (error) {
            toast.error(
              "Unable to load payment terminals. Please refresh the page or contact support if the issue persists.",
              {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 3000,
                toastId: "store-terminals-error", // prevent duplicate toasts on re-renders
              },
            );
            dataLogger.error(
              `StoreTerminals failed for dealerId: ${res.dealerId}, paymentId: ${res.paymentId}. Error: ${error}`,
            );
            reportPaymentHealthError("store-terminals", error, {
              dealerId: res.dealerId,
              paymentId: res.paymentId,
            });
          } finally {
            if (isEmpty(res.pspReference) && isEmpty(res.status)) {
              makeSignalRConnection(res.paymentId);
            } else {
              setIsPageLoading(false);
            }
          }
        }
        if (!isEmpty(res.customerPhone)) {
          if (!isValidUSPhoneNo(res.customerPhone)) {
            res.customerPhone = formatUSPhoneNo(res.customerPhone);
          }
        }
        setLinkEmail(res.customerEmail ?? "");
        setLinkPhone(res.customerPhone ?? "");
      }
    }
    await GetEmergePaySettings(token);
  }

  type RouteValidationResult = {
    isValid: boolean;
    message?: string;
  };

  function isValidRoute(
    transactionTypeId: StartTransactionType,
  ): RouteValidationResult {
    let isSaveCardURL = isSaveCardAction();
    let isPaymentURL = isPaymentAction();

    switch (transactionTypeId) {
      case StartTransactionType.SaveCard:
        if (isPaymentURL) {
          return {
            isValid: false,
            message: "SaveCard payments are not supported for Payment portal.",
          };
        }
        break;

      case StartTransactionType.CreditSale:
        if (isSaveCardURL) {
          return {
            isValid: false,
            message:
              "CreditSale payments are not supported for VaultedCard transactions.",
          };
        }
        break;

      case StartTransactionType.CreditReturn:
        if (isSaveCardURL) {
          return {
            isValid: false,
            message:
              "CreditReturn payments are not supported for VaultedCard transactions.",
          };
        }
        break;
    }

    return { isValid: true };
  }

  async function GetEmergePaySettings(token) {
    if (token.length > 50) return;

    let obj: any = new Object();
    let objBody: any = new Object();

    obj.query = "Gravity.GetTransactionDealerId";
    objBody.TransactionToken = token;
    obj.queryBody = JSON.stringify(objBody);

    const res = await TargetPayApi(
      "query",
      JSON.stringify(obj),
      "post",
      "Everest",
    );

    if (!isEmpty(res)) {
      let dealerId = res.DealerId;
      const res1 = await TargetPayApi(
        `GetDealerCredential/${dealerId}`,
        null,
        "get",
        "EmergePay",
      );
      setEmergepayIsSandBox(res1.isSandbox ? 1 : 0);
      setSignalRConnectionNegotiation(true);
      setIsPageLoading(false);
    }
  }

  function onManualPaymentStarted() {
    dataLogger.info(
      `manualPaymentStarted method called for the paymentId: ${paymentData.paymentId}`,
    );

    paymentType = "Manual";
    setIsProcessing(true);
    setCallbackPosted(false);
  }

  function onManualPaymentCompleted(response, error) {
    setPaymentDone(true);
    if (!isEmpty(error)) {
      dataLogger.error(
        `ManualPaymentCompleted method called with ${error} for the paymentId: ${paymentData.paymentId}`,
      );
      CallBackPaymentResponse(
        "failure",
        "",
        "",
        null,
        "",
        null,
        "",
        null,
        "",
        error,
      );
    }
  }

  function onACHPaymentStarted() {
    dataLogger.info(
      `achPaymentStarted method called for the paymentId: ${paymentData.paymentId}`,
    );
    paymentType = "ACH";
  }

  function onACHPaymentSubmitting() {
    setIsProcessing(true);
    setCallbackPosted(false);
  }

  function onACHPaymentCompleted(response, error) {
    setPaymentDone(true);
    if (!isEmpty(error)) {
      dataLogger.error(
        `ACHPaymentCompleted method called with ${error} for the paymentId: ${paymentData.paymentId}`,
      );
      setIsProcessing(false);
      CallBackPaymentResponse(
        "failure",
        "",
        "",
        null,
        "",
        null,
        "",
        null,
        "",
        error,
      );
    } else {
      dataLogger.info(
        `ACHPaymentCompleted successfully for the paymentId: ${paymentData.paymentId}`,
      );
      setIsProcessing(false);
      CallBackPaymentResponse("success");
    }
  }

  async function CallBackPaymentResponse(
    status: string,
    responseType: string = "Payment",
    reason: string = "",
    payment: any = null,
    cardSummary = "",
    expiryDate = "",
    authCode = "",
    amount = 0,
    paymentMethod = "",
    errorMessage = "",
    paymentPageId: string = "",
    fundingSource = "",
  ) {
    try {
      dataLogger.info(
        `CallBackPaymentResponse function called for the paymentId: ${paymentData.paymentId}`,
      );

      paymentStatus = status;
      SetManualPaymentStatus(paymentStatus);
      //for saving card only(0 payment transaction) when terminal response is received, not a webhook
      if (
        cardSummary &&
        cardSummary !== "" &&
        paymentData.requestedAmount === 0 &&
        payment === null
      ) {
        return;
      }
      paymentData.status = status;

      let avsResponseCode: string = "";
      let avsResponseText: string = "Not Available (N/A)";
      let cvvResponseCode: string = "";
      let cvvResponseText: string = "Not Available (N/A)";

      if (isEmpty(amount)) amount = 0;
      if (!isEmpty(payment)) {
        //vaultedCardPayment related
        dataLogger.info(
          `CallBackPaymentResponse in progress, payment response is ${JSON.stringify(
            payment,
          )} for the paymentId: ${paymentData.paymentId}`,
        );
        if (!isEmpty(payment.additionalData)) {
          cardSummary = payment.additionalData.cardSummary;
          expiryDate = payment.additionalData.expiryDate;
          authCode = payment.additionalData.authCode;
          paymentMethod = payment.additionalData.paymentMethod;

          if (
            payment.additionalData.avsResult !== undefined &&
            payment.additionalData.avsResult !== ""
          ) {
            avsResponseCode = payment.additionalData.avsResult.split(" ")[0];
            avsResponseText = payment.additionalData.avsResult
              .split(" ")
              .slice(1)
              .join(" ");
          }
          if (
            payment.additionalData.cvcResult !== undefined &&
            payment.additionalData.cvcResult !== ""
          ) {
            cvvResponseCode = payment.additionalData.cvcResult.split(" ")[0];
            cvvResponseText = payment.additionalData.cvcResult
              .split(" ")
              .slice(1)
              .join(" ");
          }
        } else {
          if (!isEmpty(payment.notificationItems[0].NotificationRequestItem)) {
            if (
              !isEmpty(
                payment.notificationItems[0].NotificationRequestItem
                  .additionalData,
              )
            ) {
              cardSummary = (() => {
                const additionalData =
                  payment.notificationItems[0].NotificationRequestItem
                    .additionalData;
                if (additionalData.cardSummary)
                  return additionalData.cardSummary;
                if (additionalData.bankSummary)
                  return additionalData.bankSummary;
                return "";
              })();

              expiryDate =
                payment.notificationItems[0].NotificationRequestItem
                  .additionalData.expiryDate;
              authCode =
                payment.notificationItems[0].NotificationRequestItem
                  .additionalData.authCode;

              if (
                payment.notificationItems[0].NotificationRequestItem
                  .additionalData.avsResult !== undefined &&
                payment.notificationItems[0].NotificationRequestItem
                  .additionalData.avsResult !== ""
              ) {
                const avsResult =
                  payment.notificationItems[0].NotificationRequestItem
                    .additionalData.avsResult;

                avsResponseCode = avsResult.split(" ")[0];
                avsResponseText = avsResult.split(" ").slice(1).join(" ");
              }
              if (
                payment.notificationItems[0].NotificationRequestItem
                  .additionalData.cvcResult !== undefined &&
                payment.notificationItems[0].NotificationRequestItem
                  .additionalData.cvcResult !== ""
              ) {
                const cvcResult =
                  payment.notificationItems[0].NotificationRequestItem
                    .additionalData.cvcResult;

                cvvResponseCode = cvcResult.split(" ")[0];
                cvvResponseText = cvcResult.split(" ").slice(1).join(" ");
              }
            }
            if (
              !isEmpty(
                payment.notificationItems[0].NotificationRequestItem.amount,
              )
            ) {
              amount =
                payment.notificationItems[0].NotificationRequestItem.amount
                  .value / 100;
            }
            if (
              !isEmpty(
                payment.notificationItems[0].NotificationRequestItem
                  .paymentMethod,
              )
            ) {
              paymentMethod =
                payment.notificationItems[0].NotificationRequestItem
                  .paymentMethod;
            }
          }
        }
      }
      let transactionType = getTransactionType(payment, fundingSource);
      let cashierId = JWTDecode(token, "CashierID");
      let saveCard = JWTDecode(token, "SaveCard");

      switch (status.toLowerCase()) {
        case "success":
        case "authorised":
          paymentStatus = PaymentStatus.Authorised;
          SetManualPaymentStatus(paymentStatus);
          break;
        case "failure":
          paymentStatus = PaymentStatus.Failed;
          paymentData.status = PaymentStatus.Failed;
          SetManualPaymentStatus(paymentStatus);
          break;
      }

      // If payment type is SavedCard and cardSummary is null but VaultedCardId exists, get card last four from VaultedCards
      let cardLastFour = cardSummary;
      if (
        paymentType === "SavedCard" &&
        !cardSummary &&
        !isEmpty(VaultedCardId) &&
        VaultedCards
      ) {
        const selectedCard = VaultedCards.find(
          (card: any) => card.VaultedCardId === VaultedCardId,
        );
        if (selectedCard && selectedCard.CardNumber) {
          cardLastFour = selectedCard.CardNumber;
        }
      }

      const getVaultedCardIdSearchCriteria = {
        dealerId: companyDealerId,
        customerId: paymentData.customerId,
        cardLastFour: cardLastFour,
      };

      let vaultedCardResponse = "";
      let obj: any = new Object();

      obj.VaultedCardId = vaultedCardResponse;

      // for saving card only(0 payment transaction)
      // For cardLastFour, if it's available in the payment response
      // For Manual, Terminal Payments with paramter SaveCard = true
      // For SavedCard Payments
      if (
        cardLastFour !== "" &&
        ((cardSummary &&
          cardSummary !== "" &&
          paymentData.requestedAmount === 0) ||
          (["Terminal", "Manual"].includes(paymentType) &&
            saveCard.toLowerCase() === "true") ||
          ["SavedCard"].includes(paymentType))
      ) {
        dataLogger.info(
          `CallBackPaymentResponse in progress, 0 payment transaction or paymentType condition met for the paymentId: ${paymentData.paymentId}`,
        );
        await new Promise((resolve) => setTimeout(resolve, 3000));
        vaultedCardResponse = await TargetPayApi(
          "GetVaultedCardId",
          getVaultedCardIdSearchCriteria,
          "Get",
          "VaultCard",
          true,
        );

        const guidRegex =
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        if (guidRegex.test(vaultedCardResponse)) {
          dataLogger.info(
            `CallBackPaymentResponse in progress, VaultedCardId successfully retrieved for the paymentId: ${paymentData.paymentId}`,
          );
          obj.VaultedCardId = vaultedCardResponse;
        } else {
          dataLogger.warn(
            `CallBackPaymentResponse in progress, VaultedCardId is empty for the paymentId: ${paymentData.paymentId}`,
          );
          obj.VaultedCardId = "";
        }
      }
      obj.dealerId = paymentData.dealerId;
      obj.paymentId = paymentData.paymentId;
      obj.accountCardType = paymentMethod;
      obj.accountEntryMethod = "Auto";
      obj.accountExpiryDate = expiryDate;
      obj.amount = paymentData.requestedAmount;
      obj.amountBalance = "";
      obj.amountProcessed = !surchargeAmount
        ? paymentData.requestedAmount + paymentData.platformFee
        : paymentData.requestedAmount + parseFloat(surchargeAmount) / 100;
      obj.amountTaxed = paymentData.salesTaxAmount;
      obj.amountTipped = "0";
      obj.approvalNumberResult = authCode;
      obj.avsResponseCode = avsResponseCode;
      obj.avsResponseText = avsResponseText;
      obj.batchNumber = "";
      obj.billingName = paymentData.customerName;
      obj.cashier = cashierId;
      obj.consumerSurchargeFee = !surchargeAmount
        ? paymentData.platformFee
        : parseFloat(surchargeAmount) / 100;
      obj.createdOn = new Date().toUTCString();
      obj.customerId = paymentData.customerId;
      obj.cvvResponseCode = cvvResponseCode;
      obj.cvvResponseText = cvvResponseText;
      obj.externalTransactionId = paymentData.paymentId;
      obj.isPartialApproval = false;
      obj.maskedAccount = `************${cardSummary}`;
      obj.resultMessage = paymentStatus;
      obj.resultStatus =
        paymentStatus === PaymentStatus.Authorised ? "true" : "false";
      obj.transactionReference = paymentData.invoiceId;
      obj.transactionType = transactionType;
      obj.uniqueTransId = paymentData.paymentId;
      obj.errorMessage = errorMessage;
      obj.paymentType = paymentType;
      obj.terminalId = paymentData.POIID ?? "";

      if (paymentType === "PayByLink") {
        obj.email = linkEmail;
        obj.phone = linkPhone;
        obj.paymentLinkUrl = paymentLinkUrl;
      }

      let res: any = new Object();
      res.isApproved =
        paymentStatus === PaymentStatus.Authorised ? "true" : "false";
      res.transactionResponse = obj;
      let urlParams = new URLSearchParams(window.location.search);
      res.isMobile = urlParams.get("ismobile");
      res.paymentPageId = paymentPageId;

      let jsonString = JSON.stringify(res);
      dataLogger.info(
        `CallBackPaymentResponse in progress, payload for the callback is: ${jsonString}`,
      );
      // Call SavePaymentResponse and handle it with then() and catch()
      SavePaymentResponse(jsonString)
        .then((response) => {
          Callback(res, paymentData.hostUrl);
          paymentProcessed = true;
          setCallbackPosted(true);
        })
        .catch((error) => {
          dataLogger.error(
            `CallBackPaymentResponse in progress, SavePaymentResponse Responded with failure for the paymentId: ${paymentData.paymentId} with errors ${error}`,
          );
          const errorMessage =
            error instanceof Error
              ? error.message
              : "An unknown error occurred";
          obj.errorMessage =
            obj.errorMessage +
            `Unable to Save Payment Response in Target Pay due to: ${errorMessage}`;
          Callback(res, paymentData.hostUrl);
          paymentProcessed = true;
          setCallbackPosted(true);
        });

      toast.dismiss();
      if (paymentStatus === PaymentStatus.Authorised)
        toast.success("Status: Authorized", {
          position: toast.POSITION.TOP_CENTER,
        });
      if (paymentStatus === PaymentStatus.Failed && isEmpty(paymentPageId)) {
        if (isEmpty(errorMessage)) errorMessage = paymentData.status;
        toast.error("Status: " + errorMessage, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: false,
          closeOnClick: true,
        });
      }
    } catch (error) {
      dataLogger.error(
        `CallBackPaymentResponse execution failed with error: ${JSON.stringify(
          error,
        )} for the paymentId: ${paymentData.paymentId}`,
      );
    }
  }

  function getTransactionType(payment, fundingSource) {
    var transactionType = JWTDecode(token, "TransactionType");
    if (transactionType !== CardType.CreditSale) {
      return transactionType;
    }
    const isDebit = (value: string | undefined): boolean =>
      !isEmpty(value) && value.toLowerCase().includes("debit");
    if (!isEmpty(fundingSource) && isDebit(fundingSource)) {
      return CardType.DebitSale;
    }
    if (isEmpty(payment)) {
      return transactionType;
    }

    const additionalData = !isEmpty(payment.additionalData)
      ? payment.additionalData
      : payment.notificationItems[0].NotificationRequestItem.additionalData;

    if (
      isDebit(additionalData["fundingSource"]) ||
      isDebit(additionalData["checkout.cardAddedBrand"])
    ) {
      return CardType.DebitSale;
    }
    return JWTDecode(token, "TransactionType");
  }

  function isMobile() {
    let urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("ismobile") === "true";
  }

  function CheckPayment(res: any) {
    dataLogger.info(
      `CheckPayment Method Called after terminal payment with response: ${res} for the PaymendId: ${paymentData.paymentId}`,
    );
    const obj = JSON.parse(res);
    const myArray = obj.AdditionalResponse.split("&");
    let cardSummary = "",
      cardBin = "",
      expiryMonth = "",
      expiryYear = "",
      paymentMethod = "",
      fundingSource = "",
      authCode = "",
      posAuthAmountValue = 0;
    for (let i = 0; i <= myArray.length - 1; i++) {
      let values = myArray[i].split("=");
      if (values.length == 2) {
        if (values[0] === "acquirerResponseCode") {
          var decodedValue = decodeURIComponent(values[1]);
          if (!decodedValue.toLowerCase().includes("approved")) {
            dataLogger.info(
              `CheckPayment Method in progress, acquirerResponseCode: ${decodedValue}`,
            );
            break;
          }
        }
        if (values[0] === "cardSummary") cardSummary = values[1];
        if (values[0] === "cardBin") cardBin = values[1];
        if (values[0] === "expiryMonth") expiryMonth = values[1];
        if (values[0] === "expiryYear") expiryYear = values[1];
        if (values[0] === "posAuthAmountValue")
          posAuthAmountValue = values[1] / 100;
        if (values[0] === "paymentMethod") paymentMethod = values[1];
        if (values[0] === "fundingSource") fundingSource = values[1];
        if (values[0] === "surchargeAmount") surchargeAmount = values[1];
        if (values[0] === "authCode") authCode = values[1];
      }
    }
    paymentData.status = obj.Result;
    if (cardSummary !== "" || obj.Result === "Failure") {
      dataLogger.info(
        `CheckPayment Method, CallbackPaymentResponse on next line Call with Result: ${obj.Result} for the PaymendId: ${paymentData.paymentId}`,
      );
      CallBackPaymentResponse(
        obj.Result,
        "",
        "",
        null,
        cardSummary,
        expiryMonth + "/" + expiryYear,
        authCode,
        posAuthAmountValue,
        paymentMethod,
        // decodedValue holds the raw acquirerResponseCode text regardless of outcome (it can read
        // "...Approved" on success), so only ever treat it as a decline reason when the terminal
        // result was actually a failure.
        resolveDeclineErrorMessage(
          obj.Result === "Failure" ? decodedValue : "",
          paymentType,
        ),
        "",
        fundingSource,
      );
    }
  }

  async function ProcessPayment(isSaveTab = false) {
    // TIER 3 SAFETY: never charge before ValidateAdyenPaymentToken has confirmed
    // the payment is still payable (guards the optimistic-render window).
    if (!paymentValidated) return;
    dataLogger.info(
      `ProcessPayment method called to process the terminal payment for the PaymendId: ${paymentData.paymentId}`,
    );

    let paymentInitEvent = {
      paymentInit: true,
      paymentId: paymentData.paymentId,
      dealerId: paymentData.dealerId,
    };
    Callback(JSON.stringify(paymentInitEvent), paymentData.hostUrl);

    if (isSaveTab === true) paymentData.SaveCard = true;
    toast.dismiss();
    paymentType = "Terminal";
    setIsProcessing(true);
    setCallbackPosted(false);
    paymentData.POIID = terminal;
    paymentData.Status = "";
    paymentData.pspReference = "";

    // WAF: dealerInfo is resolved server-side from DealerId and is not read by
    // the AdyenTerminalPayment endpoint. Omit it from the outgoing payload.
    const { dealerInfo, ...paymentPayload } = paymentData;

    let res = await TargetPayApi(
      "AdyenTerminalPayment",
      paymentPayload,
      "post",
      "PerseusPay",
      true,
    );

    if (!isEmpty(res)) {
      if (typeof res !== "string") {
        // TargetPayApi returned a raw AxiosError (returnError = true path) -- not a JSON response string.
        // Calling JSON.parse on an AxiosError coerces it via toString() to "AxiosError: ..."
        // causing: SyntaxError: Unexpected token 'A', "AxiosError"... is not valid JSON
        const errorMsg =
          res?.response?.data?.Error ||
          res?.response?.data?.message ||
          res?.message ||
          "Terminal payment failed";
        dataLogger.error(
          `ProcessPayment method, AdyenTerminalPayment network/server error for PaymentId: ${paymentData.paymentId}: ${errorMsg}`,
        );
        // Structured RUM error for the AxiosError=0 guardrail (§5b). Record the
        // response body LENGTH only — never its content (PCI).
        let responseBodyLength = 0;
        try {
          const data = res?.response?.data;
          responseBodyLength =
            data == null
              ? 0
              : typeof data === "string"
                ? data.length
                : JSON.stringify(data).length;
        } catch {
          responseBodyLength = -1;
        }
        reportPaymentHealthError("adyen-terminal-payment", res, {
          httpStatus: res?.response?.status,
          responseBodyLength,
        });
        setIsProcessing(false);
        CallBackPaymentResponse(
          "failure",
          "",
          "",
          null,
          "",
          null,
          "",
          null,
          "",
          errorMsg,
        );
        return;
      }
      let obj = JSON.parse(res);
      if (!isEmpty(obj.Error)) {
        dataLogger.error(
          `ProcessPayment method, AdyenTerminalPayment Responded with errors: ${obj.Error} for the PaymendId: ${paymentData.paymentId}`,
        );
        setIsProcessing(false);
        CallBackPaymentResponse(
          "failure",
          "",
          "",
          null,
          "",
          null,
          "",
          null,
          "",
          obj.Error,
        );
        return;
      }
      CheckPayment(res);
      setPaymentDone(true);
    }
  }

  async function SavePaymentResponse(paymentResponse) {
    try {
      dataLogger.info(
        `SavePaymentResponse method called for the PaymendId: ${paymentData.paymentId}`,
      );

      toast.dismiss();
      setIsProcessing(true);
      setCallbackPosted(false);

      let res = await TargetPayApi("SavePaymentResponse", paymentResponse);

      dataLogger.info(
        `SavePaymentResponse method responded with: ${JSON.stringify(
          res,
        )} for the PaymendId: ${paymentData.paymentId}`,
      );

      setIsProcessing(false);

      // Check if the response is not empty and statusCode is OK
      if (res && res.statusCode !== "OK") {
        console.log("SavePaymentResponse Method responded with error");
        toast.error("Error: ", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: false,
          closeOnClick: true,
        });
        return; // Return early if there's an error
      }
      console.log("SavePaymentResponse Method responded with success");
      return res; // Return the response in case it's successful
    } catch (error) {
      setIsProcessing(false);
      toast.error("An error occurred while saving payment response.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: false,
        closeOnClick: true,
      });
    }
  }

  async function GetCustomerToken(dealerId, customerId) {
    setVaultedCardsLoading(true);
    try {
      let obj: any = new Object();
      let objBody: any = new Object();

      obj.query = "PerseusPay.VaultedCards";
      objBody.DealerId = dealerId;
      objBody.CustomerId = customerId;

      obj.queryBody = JSON.stringify(objBody);

      const response = await TargetPayApi(
        "query",
        JSON.stringify(obj),
        "post",
        "Everest",
      );

      if (!isEmpty(response) && response.length > 0) {
        SetVaultedCards(response);
        SetVaultedCardId(response[0].VaultedCardId);
      }
    } finally {
      setVaultedCardsLoading(false);
    }
  }

  function AmountLabel() {
    let label = "Amount";
    if (tokenClaims.TransactionTypeId === "1") label = "Credit Return";
    return label;
  }

  function SaveCard() {
    return (
      <span className={classes.defaultFont}>
        {paymentData.saveCard &&
          tokenClaims.TransactionType != "CreditReturn" && (
            <span>
              Save Card&nbsp;&nbsp;<>&#x2713;</>
            </span>
          )}
      </span>

      //   <FormControlLabel style={{ margin: "0px" }}
      //     control={
      //       <Checkbox
      //         disabled={true}
      //         checked={paymentData.saveCard}
      //         name="AllowTextToPay"
      //       />
      //     }
      //     label="Save Card"
      //   />
      //
    );
  }

  function isNumeric(value) {
    return /^-?\d+$/.test(value);
  }

  async function sendLink() {
    // TIER 3 SAFETY: don't send a pay-by-link before validation confirms the
    // payment is valid/pending.
    if (!paymentValidated) return;
    dataLogger.info(
      `SendLink Method called to send pay by link email for the PaymendId: ${paymentData.paymentId}`,
    );

    let phone = linkPhone;
    if (!isEmpty(phone)) {
      if (!isValidUSPhoneNo(phone)) {
        phone = formatUSPhoneNo(phone);
      }
    }

    paymentType = "PayByLink";
    setIsProcessing(true);
    setCallbackPosted(false);

    let obj: any = new Object();
    obj.sendType = "*";
    obj.token = paymentToken;
    obj.CustomerCell = phone;
    if (ValidateEmail(linkEmail)) obj.CustomerEmail = linkEmail;
    obj.DealerId = paymentData.dealerId;
    obj.CustomerId = paymentData.customerId;
    obj.PaymentObject = PaymentLinkResponse;
    obj.CashierID = JWTDecode(paymentToken, "CashierID");
    obj.CustomerName = JWTDecode(paymentToken, "CustomerName");
    // obj.Memo = JWTDecode(paymentToken, 'Memo');
    // obj.MoreInfo = JWTDecode(paymentToken, 'MoreInfo');
    let jsonString = JSON.stringify(obj);

    const res = await TargetPayApi("AdyenPaymentLink", jsonString);
    if (!res.isRequestSucceeded) {
      dataLogger.error(
        `SendLink Method in progress responded with error: ${JSON.stringify(
          res.response,
        )} for the PaymendId: ${paymentData.paymentId}`,
      );
      toast.dismiss();
      toast.error("Error: " + res.response, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: false,
        closeOnClick: true,
      });
    } else if (!isEmpty(res) || !isEmpty(res.paymentObject)) {
      dataLogger.info(
        `SendLink Method in progress responded with success: ${JSON.stringify(
          res.paymentObject,
        )} for the PaymendId: ${paymentData.paymentId}`,
      );
      if (!isEmpty(res.moreInfo)) {
        setMoreInfo(res.moreInfo);
      } else {
        setMoreInfo("");
      }

      SetPaymentLinkResponse(res.paymentObject);
      paymentLinkUrl = res.url ?? "";
      CallBackPaymentResponse(
        "Link Sent",
        "",
        "",
        null,
        "",
        null,
        "",
        null,
        "",
        "",
        res.id,
      );

      return;
    }
    setIsProcessing(false);
  }

  async function SendMessage(sendType: string) {
    let obj: any = new Object();
    obj.sendType = sendType;
    obj.token = paymentToken;
    obj.CustomerCell = linkPhone;
    obj.CustomerEmail = linkEmail;
    obj.DealerId = paymentData.dealerId;
    obj.CustomerId = paymentData.customerId;
    obj.PaymentObject = PaymentLinkResponse;
    let jsonString = JSON.stringify(obj);
    const res = await TargetPayApi("AdyenPaymentLink", jsonString);
    setIsProcessing(false);
    if (!isEmpty(res) || !isEmpty(res.PaymentObject)) {
      SetPaymentLinkResponse(res.PaymentObject);
      makeSignalRConnection(paymentData.paymentId);
    }
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  function GetTokenLabel(): any {
    if (!isEmpty(VaultedCards) && VaultedCards.length > 0)
      return `${VaultedCards.length}`;
  }

  async function ProcessTokenPayment() {
    // TIER 3 SAFETY: never charge a vaulted card before validation confirms the
    // payment is still payable.
    if (!paymentValidated) return;
    dataLogger.info(
      `ProcessTokenPayment method called to process a vaulted payment for the PaymendId: ${paymentData.paymentId}`,
    );

    let obj: any = new Object();
    obj.VaultedCardId = VaultedCardId;
    obj.token = paymentToken;
    let jsonString = JSON.stringify(obj);

    paymentType = "SavedCard";
    setIsProcessing(true);
    setCallbackPosted(false);

    if (tokenClaims.TransactionTypeId === "1") {
      const res = await TargetPayApi(
        "ProcessAdyenVaultedCreditReturn",
        jsonString,
        "post",
        "PerseusPay",
        true,
        false,
      );

      // NOTE: intentionally do NOT setIsProcessing(false) here. Mirroring the
      // Terminal flow (ProcessPayment), the button stays disabled until the
      // payment response is fully processed (paymentDone / callback posted),
      // preventing the brief re-enable that allowed a duplicate submit.

      if (!isEmpty(res)) {
        if (res.status !== "Success") {
          dataLogger.error(
            `ProcessAdyenVaultedCreditReturn responded with error: ${res.message} for the PaymendId: ${paymentData.paymentId}`,
          );
          CallBackPaymentResponse(
            "failure",
            "",
            "",
            null,
            "",
            null,
            "",
            null,
            "",
            res.message,
          );
          return;
        }

        setPaymentDone(true);
        CallBackPaymentResponse("Success", "", "", null, "", "", "", 0, "", "");
      }
    } else {
      const res = await TargetPayApi("ProcessAdyenVaultedPayment", jsonString);
      // NOTE: intentionally do NOT setIsProcessing(false) here. Mirroring the
      // Terminal flow (ProcessPayment), the button stays disabled until the
      // payment response is fully processed (paymentDone / callback posted),
      // preventing the brief re-enable that allowed a duplicate submit.

      if (!isEmpty(res)) {
        if (res.hasOwnProperty("errorCode") && !isEmpty(res.errorCode)) {
          dataLogger.error(
            `ProcessTokenPayment in progress responded with error: ${res.message} for the PaymendId: ${paymentData.paymentId}`,
          );
          CallBackPaymentResponse(
            "failure",
            "",
            "",
            null,
            "",
            null,
            "",
            null,
            "",
            res.message,
          );
          return;
        }
        let Reason = "";
        if (res.hasOwnProperty("refusalReason")) Reason = res.refusalReason;
        setPaymentDone(true);
        // Only treat Reason as a decline reason when the payment actually wasn't authorised —
        // refusalReason should only be present on a refusal, but gate explicitly rather than rely
        // on that alone.
        const isDeclined = res.resultCode !== "Authorised";
        CallBackPaymentResponse(
          res.resultCode,
          "Payment",
          Reason,
          res,
          "",
          "",
          "",
          0,
          "",
          resolveDeclineErrorMessage(isDeclined ? Reason : "", paymentType),
        );
      }
    }
  }
  function OnTextChange(value: any, key: any) {
    paymentData[key] = value;
  }

  function onTerminalChange(e) {
    setTerminal(e);
  }

  function onVoidPayment(e) {
    setPaymentDone(true);
  }

  function CheckActiveTermininalInMin(row: any): any {
    if (isEmpty(row.lastActivityAt)) return 9999;
    var date1: any = Date.parse(row.lastActivityAt);
    var data2: any = new Date();
    var diffMs = data2 - date1; // milliseconds between now & Christmas
    var diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000); // minutes
    return diffMins;
  }

  function CheckActiveTerminal(row: any): any {
    // if (isEmpty(row.lastActivityAt)) return "";
    // var date1: any = Date.parse(row.lastActivityAt);
    // var data2: any = new Date();
    // var diffMs = (data2 - date1); // milliseconds between now & Christmas
    // var diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000); // minutes
    let diffMins = CheckActiveTermininalInMin(row);
    let res = `Now`;
    if (diffMins > 24 * 60) return "";
    if (diffMins > 0) res = `${diffMins}`;
    if (diffMins < 2) return `(Active)`;
    if (diffMins < 60) return `(Active ${res}m)`;
    return "";
  }

  const renderManualPaymentStatus = () => {
    if (
      manualPaymentStatus === PaymentStatus.Success ||
      manualPaymentStatus === PaymentStatus.Authorised
    ) {
      return (
        <span style={{ color: "green" }}>
          Payment successfully processed...
        </span>
      );
    } else if (manualPaymentStatus === PaymentStatus.LinkSent) {
      return <span style={{ color: "green" }}>Payment Link Sent...</span>;
    } else if (
      manualPaymentStatus === PaymentStatus.Failed ||
      !isEmpty(manualPaymentStatus)
    ) {
      return <span style={{ color: "red" }}>Payment failed...</span>;
    }
    return null; // Return null if no conditions are met
  };

  const getTerminalFinalizingText = () => {
    const isAuthorized =
      paymentStatus === PaymentStatus.Authorised ||
      paymentStatus === PaymentStatus.Success ||
      manualPaymentStatus === PaymentStatus.Authorised ||
      manualPaymentStatus === PaymentStatus.Success ||
      paymentData?.status === PaymentStatus.Authorised ||
      paymentData?.status === PaymentStatus.Success;

    if (isAuthorized) {
      return "Payment authorized. Finalizing and notifying host...";
    }

    return "Payment response received. Finalizing and notifying host...";
  };

  useEffect(() => {
    if (typeof tabValue === "string") {
      const normalizedTabValue = tabValue.toLowerCase();

      if (normalizedTabValue === "manual") {
        setManualTabVisited(true);
      }

      if (normalizedTabValue === "savecard" && isSaveCardAction()) {
        setSavecardTabVisited(true);
      }
    }
  }, [tabValue]);

  return (
    <>
      {/* initial payment page loader */}
      {!signalRconnectionNegotiation && !pagePainted && isPageLoading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <CircularProgress />
        </Box>
      )}
      {(signalRconnectionNegotiation || pagePainted) && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          style={{ padding: "10px" }}
        >
          {isEmpty(paymentToken) && !isProcessing && (
            <Box
              style={{
                border: "1px solid silver",
                padding: "5px 10px",
                marginTop: "20px",
              }}
            >
              <h3>Invalid or missing payment token...</h3>
            </Box>
          )}
          {!isEmpty(errorMessage) && (
            <Box
              style={{
                border: "1px solid #E5E7E9",
                padding: "5px 10px",
                marginTop: "20px",
                color: "red",
                backgroundColor: "#F8F9F9",
                borderRadius: "10px",
              }}
            >
              <h3>{errorMessage}</h3>
            </Box>
          )}

          {isEmpty(paymentData) &&
            !isEmpty(paymentToken) &&
            paymentData.length > 50 && (
              <Box
                style={{
                  border: "1px solid silver",
                  padding: "5px 10px",
                  marginTop: "20px",
                }}
              >
                <h2>Invalid/Expired payment request</h2>
              </Box>
            )}
          {!isEmpty(token) && EmergepayIsSandBox > -1 && token.length < 50 && (
            <Box>
              <iframe
                style={{
                  border: "0px solid red",
                  width: "700px",
                  height: "570px",
                }}
                title="static_html"
                src={`emergePay.html?isSandBox=${EmergepayIsSandBox}&transactionToken=${token}`}
              ></iframe>
            </Box>
          )}

          {!isEmpty(paymentData) && token.length > 50 && (
            <Box sx={{ width: "100%", maxWidth: 760 }}>
              <PaymentScreenHeader
                invoiceId={paymentData.invoiceId ?? null}
                requestedAmount={paymentData.requestedAmount ?? null}
                platformFee={paymentData.platformFee ?? null}
                totalAmount={paymentData.totalAmount ?? null}
                platformFeeFlag={PlatformFeeFlag}
                amountLabel={AmountLabel()}
                status={manualPaymentStatus || null}
                customerName={paymentData.customerName ?? null}
                customerId={paymentData.customerId ?? null}
                customerEmail={paymentData.customerEmail ?? null}
                customerPhone={paymentData.customerPhone ?? null}
                isProcessing={isProcessing}
                callbackPosted={callbackPosted}
                isSaveCardFlow={isSaveCardAction()}
              />
              <Tabs
                style={{ display: ShowTabs() ? "" : "none" }}
                value={tabValue}
                onChange={handleTabChange}
                aria-label="basic tabs example"
                TabIndicatorProps={{
                  style: { height: 3, borderRadius: "3px 3px 0 0" },
                }}
                sx={{
                  minHeight: 44,
                  "& .MuiTabs-flexContainer": {
                    gap: 0.5,
                  },
                  "& .MuiTab-root": {
                    minHeight: 44,
                    px: 1.5,
                    fontSize: "0.78rem",
                    fontWeight: 500,
                    letterSpacing: 0.5,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    transition: "color 0.15s",
                    minWidth: "unset",
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: "primary.main",
                    fontWeight: 700,
                  },
                  "& .MuiTab-root.Mui-disabled": {
                    opacity: 0.4,
                  },
                }}
              >
                {(CheckSaveCardTabs("Savecard") ||
                  CheckSaveCardTabs("Manual")) && (
                  <Tab label="Save Card" value="Savecard" />
                )}

                {CheckTab("Terminal") && (
                  <Tab
                    disabled={paymentData.void || isProcessing}
                    label="Terminal"
                    value="Terminal"
                  />
                )}
                {(CheckSaveCardTabs("Savecardterminal") ||
                  CheckSaveCardTabs("Terminal")) && (
                  <Tab
                    disabled={paymentData.void || isProcessing}
                    label="Save Card Terminal"
                    value="Savecardterminal"
                  />
                )}
                {CheckTab("Manual") && (
                  <Tab
                    disabled={paymentData.void || isProcessing}
                    label="Manual"
                    value="Manual"
                  />
                )}
                {isACHPaymentEnabled && CheckTab("Ach") && (
                  <Tab
                    disabled={paymentData.void || isProcessing}
                    label="ACH Direct Debit"
                    value="Ach"
                  />
                )}
                {CheckTab("Sendlink") && (
                  <Tab
                    disabled={paymentData.void || isProcessing}
                    label="Link to pay"
                    value="Sendlink"
                  />
                )}
                {CheckTab("Token") && (
                  <Tab
                    disabled={paymentData.void || isProcessing}
                    label={
                      <Badge badgeContent={GetTokenLabel()} color="primary">
                        Saved Cards&nbsp;&nbsp;
                      </Badge>
                    }
                    value="Token"
                  />
                )}
                {CheckTab("Payments") &&
                  paymentData &&
                  !isEmpty(paymentData.transactions) &&
                  paymentData.transactions > 0 && (
                    <Tab
                      disabled={isProcessing}
                      label={
                        <Badge
                          badgeContent={paymentData.transactions}
                          color="primary"
                        >
                          Payments&nbsp;&nbsp;
                        </Badge>
                      }
                      value="Payments"
                    />
                  )}
              </Tabs>
              <Box>
                {/* AdyenTransactions */}
                {singleSessionSaveCardTabEnabled
                  ? saveCardTabVisited && (
                      <Box
                        style={{
                          display:
                            tabValue.toLowerCase() === "savecard"
                              ? "block"
                              : "none",
                        }}
                      >
                        {paymentProcessed && (
                          <Box className={classes.paymentBox}>
                            {(paymentStatus === "Success" ||
                              paymentStatus === "Authorised") && (
                              <h4>
                                <span style={{ color: "green" }}>
                                  Card Saved...
                                </span>
                              </h4>
                            )}
                            {(paymentStatus === "Failed" ||
                              paymentStatus !== "Authorised") &&
                              !isEmpty(manualPaymentStatus) && (
                                <h4>
                                  <span style={{ color: "red" }}>
                                    Saving Card failed...
                                  </span>
                                </h4>
                              )}
                          </Box>
                        )}
                        {!paymentProcessed && (
                          <Box className={classes.paymentBoxManual}>
                            <Box>
                              <Suspense fallback={<LazyFallback />}>
                                <PaymentContainer
                                  validated={paymentValidated}
                                  token={paymentToken}
                                  onPaymentStarted={onManualPaymentStarted}
                                  onPaymentCompleted={onManualPaymentCompleted}
                                  saveCard={true}
                                />
                              </Suspense>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )
                  : tabValue === "Savecard" && (
                      <Box>
                        {paymentProcessed && (
                          <Box className={classes.paymentBox}>
                            {(paymentStatus === "Success" ||
                              paymentStatus === "Authorised") && (
                              <h4>
                                <span style={{ color: "green" }}>
                                  Card Saved...
                                </span>
                              </h4>
                            )}
                            {(paymentStatus === "Failed" ||
                              paymentStatus !== "Authorised") &&
                              !isEmpty(manualPaymentStatus) && (
                                <h4>
                                  <span style={{ color: "red" }}>
                                    Saving Card failed...
                                  </span>
                                </h4>
                              )}
                          </Box>
                        )}
                        {!paymentProcessed && (
                          <Box className={classes.paymentBoxManual}>
                            <Box>
                              <Suspense fallback={<LazyFallback />}>
                                <PaymentContainer
                                  validated={paymentValidated}
                                  token={paymentToken}
                                  onPaymentStarted={onManualPaymentStarted}
                                  onPaymentCompleted={onManualPaymentCompleted}
                                  saveCard={true}
                                />
                              </Suspense>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                {tabValue === "Payments" && (
                  <Box className={classes.paymentBox}>
                    <Suspense fallback={<LazyFallback />}>
                      <AdyenTransactions
                        paymentData={paymentData}
                        terminals={Terminals()}
                        terminal={terminal}
                        onTerminalChange={onTerminalChange}
                        onVoidPayment={onVoidPayment}
                      ></AdyenTransactions>
                    </Suspense>
                  </Box>
                )}
                {tabValue === "Token" && (
                  <Box className={classes.paymentBox}>
                    {VaultedCards && (
                      <Box
                        style={{
                          paddingTop: "10px",
                          borderTop: "1px solid silver",
                        }}
                      >
                        {VaultedCards.map((row: any, index: any) => {
                          const dateSetupLocal = formatUtcLocalPattern(
                            row.DateSetup,
                          );
                          return (
                            <Box
                              style={{
                                borderBottom: "1px solid #E5E7E9",
                                marginBottom: "5px",
                                padding: "5px 10px",
                                cursor: "pointer",
                                backgroundColor:
                                  row.VaultedCardId === VaultedCardId
                                    ? "#D4E6F1"
                                    : "",
                              }}
                              onClick={(e) =>
                                SetVaultedCardId(row.VaultedCardId)
                              }
                              key={index}
                            >
                              <table>
                                <tbody>
                                  <tr>
                                    <td>
                                      <Checkbox
                                        checked={
                                          row.VaultedCardId === VaultedCardId
                                        }
                                      />
                                    </td>
                                    <td>
                                      <Box>Card Type: {row.CardType}</Box>
                                      <Box>
                                        Card Number: *****{row.CardNumber} (
                                        {format(
                                          new Date(row.ExpiryDate),
                                          "MM/yyyy",
                                        )}
                                        )
                                      </Box>
                                      {dateSetupLocal && (
                                        <Box>Date Setup: {dateSetupLocal}</Box>
                                      )}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </Box>
                          );
                        })}
                        <Box
                          style={{
                            margin: "10px 0px",
                            textAlign: "right",
                            marginRight: "10px",
                          }}
                        >
                          <Button
                            style={{ textTransform: "none" }}
                            disabled={
                              isProcessing || paymentDone || paymentProcessed
                            }
                            onClick={ProcessTokenPayment}
                            variant="contained"
                          >
                            {paymentProcessed ? (
                              <>
                                Payment{" "}
                                {paymentData.status.indexOf("Fail") >= 0
                                  ? paymentData.status
                                  : "Processed"}{" "}
                              </>
                            ) : (
                              <>
                                {!isProcessing && (
                                  <span>
                                    {tokenClaims.TransactionTypeId === "1"
                                      ? "Return "
                                      : "Pay "}
                                    {dollarUSLocale.format(
                                      paymentData.totalAmount,
                                    )}
                                  </span>
                                )}
                                {isProcessing && (
                                  <span>
                                    {paymentDone
                                      ? getTerminalFinalizingText()
                                      : `Waiting for the payment (${dollarUSLocale.format(
                                          paymentData.totalAmount,
                                        )})`}
                                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                    <CircularProgress
                                      size="1rem"
                                      style={{
                                        color:
                                          paymentDone === true
                                            ? "green"
                                            : "orange",
                                      }}
                                    ></CircularProgress>
                                  </span>
                                )}
                              </>
                            )}
                          </Button>
                        </Box>
                      </Box>
                    )}
                    {!VaultedCards && vaultedCardsLoading && <LazyFallback />}
                    {!VaultedCards && !vaultedCardsLoading && (
                      <Box style={{ padding: "10px" }}>
                        <h4>No card saved</h4>
                      </Box>
                    )}
                  </Box>
                )}
                {tabValue === "Sendlink" && (
                  // <Paper>
                  <Box className={classes.paymentBox}>
                    <Box style={formStyles.fieldSpacer}>
                      <TextField
                        size="small"
                        label="Email"
                        style={{ marginTop: "5px" }}
                        fullWidth
                        id="outlined-required"
                        value={linkEmail}
                        onChange={(e) => setLinkEmail(e.target.value)}
                        InputLabelProps={{ className: classes.defaultFont }}
                      />
                    </Box>
                    <Box style={formStyles.fieldSpacer}>
                      <InputMask
                        mask="+1 (999) 999 9999"
                        alwaysShowMask={false}
                        value={linkPhone}
                        onChange={(e) => setLinkPhone(e.target.value)}
                      >
                        {() => (
                          <TextField
                            size="small"
                            autoFocus
                            label="Cell Phone"
                            fullWidth
                            id="outlined-required"
                            InputLabelProps={{ className: classes.defaultFont }}
                          />
                        )}
                      </InputMask>
                    </Box>
                    <Box>
                      <Button
                        style={{ marginTop: "20px" }}
                        fullWidth
                        onClick={(e) => sendLink()}
                        disabled={
                          (ValidatePhoneNumber(linkPhone) ||
                          ValidateEmail(linkEmail)
                            ? false
                            : true) ||
                          isProcessing ||
                          paymentProcessed
                        }
                        variant="contained"
                      >
                        Send Link
                      </Button>
                    </Box>
                    <Box>
                      {PaymentLinkResponse && (
                        <Box style={{ marginTop: "15px" }}>
                          <Box>Payment Link: {PaymentLinkResponse.url}</Box>
                          <Box>
                            Expires At:{" "}
                            {format(
                              new Date(PaymentLinkResponse.expiresAt),
                              "M/d/yyyy h:mm a",
                            )}
                          </Box>
                          <Box>Payment Id: {PaymentLinkResponse.id}</Box>
                          {moreInfo && (
                            <Box
                              style={{ color: "#ed6c02", textAlign: "right" }}
                            >
                              <h5>{moreInfo}</h5>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
                {isACHPaymentEnabled && tabValue === "Ach" && (
                  <Box>
                    {/* TIER 3 SAFETY: ACH form self-submits — gate on validation. */}
                    {paymentValidated ? (
                      <Suspense fallback={<LazyFallback />}>
                        <ACHPaymentForm
                          PaymentData={paymentData}
                          OnACHPaymentStarted={onACHPaymentStarted}
                          OnACHPaymentSubmitting={onACHPaymentSubmitting}
                          OnACHPaymentCompleted={onACHPaymentCompleted}
                          IsMobile={isMobile()}
                          PaymentProcessed={paymentDone || paymentProcessed}
                          renderManualPaymentStatus={renderManualPaymentStatus}
                        />
                      </Suspense>
                    ) : (
                      <VerifyingPayment />
                    )}
                  </Box>
                )}
                {/* Gate the Manual panel on CheckTab so it never mounts (and never
                    fires AdyenPaymentSession) when Manual is disallowed — e.g.
                    CreditReturn, which is Terminal/Token only. Guards both the
                    single-session (display:none, stays mounted) and the plain
                    branch, and neutralizes a stale manualTabVisited flag. */}
                {CheckTab("Manual") &&
                  (singleSessionManualTabEnabled
                    ? manualTabVisited && (
                        <Box
                          style={{
                            display: tabValue === "Manual" ? "block" : "none",
                          }}
                        >
                          {paymentProcessed && (
                            <Box className={classes.paymentBox}>
                              <h4>{renderManualPaymentStatus()}</h4>
                            </Box>
                          )}
                          {!paymentProcessed && (
                            <Box className={classes.paymentBoxManual}>
                              <Box>
                                <Suspense fallback={<LazyFallback />}>
                                  <PaymentContainer
                                    validated={paymentValidated}
                                    token={paymentToken}
                                    onPaymentStarted={onManualPaymentStarted}
                                    onPaymentCompleted={
                                      onManualPaymentCompleted
                                    }
                                    saveCard={paymentData.saveCard}
                                  />
                                </Suspense>
                              </Box>

                              <Box style={{ textAlign: "right" }}>
                                <SaveCard></SaveCard>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )
                    : tabValue === "Manual" && (
                        <Box>
                          {paymentProcessed && (
                            <Box className={classes.paymentBox}>
                              <h4>{renderManualPaymentStatus()}</h4>
                            </Box>
                          )}
                          {!paymentProcessed && (
                            <Box className={classes.paymentBoxManual}>
                              <Box>
                                <Suspense fallback={<LazyFallback />}>
                                  <PaymentContainer
                                    validated={paymentValidated}
                                    token={paymentToken}
                                    onPaymentStarted={onManualPaymentStarted}
                                    onPaymentCompleted={
                                      onManualPaymentCompleted
                                    }
                                    saveCard={paymentData.saveCard}
                                  />
                                </Suspense>
                              </Box>

                              <Box style={{ textAlign: "right" }}>
                                <SaveCard></SaveCard>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      ))}
                {tabValue === "Terminal" && (
                  <Box>
                    <Box
                      className={classes.paymentBox}
                      style={{ minWidth: !isMobile() ? "400px" : "100px" }}
                    >
                      <Box style={{ border: "0px solid red" }}>
                        <Box style={{ marginTop: "15px" }}>
                          <FormControl size="small" fullWidth>
                            <InputLabel id="filter-type-label">
                              Payment Terminal
                            </InputLabel>
                            <Select
                              fullWidth
                              labelId="filter-type-label"
                              id="filter-type-select"
                              value={terminal}
                              onChange={(e) =>
                                setTerminal(e.target.value as string)
                              }
                              label="Payment Terminal 123"
                            >
                              <MenuItem value="0">Choose One</MenuItem>
                              {Terminals().map((row: any, index: number) => (
                                <MenuItem value={row.id} key={index}>
                                  <table>
                                    <tbody>
                                      <tr>
                                        <td style={{ verticalAlign: "bottom" }}>
                                          {CheckActiveTermininalInMin(row) >
                                            60 && (
                                            <ClearOutlinedIcon
                                              style={{
                                                fontSize: "15",
                                                color: "red",
                                                marginRight: "10px",
                                              }}
                                            ></ClearOutlinedIcon>
                                          )}

                                          {CheckActiveTermininalInMin(row) <
                                            60 && (
                                            <CheckIcon
                                              style={{
                                                fontSize: "15",
                                                color:
                                                  CheckActiveTermininalInMin(
                                                    row,
                                                  ) < 10
                                                    ? "green"
                                                    : "orange",
                                                marginRight: "10px",
                                              }}
                                            ></CheckIcon>
                                          )}
                                        </td>
                                        <td>{row.terminalName}</td>
                                        <td>
                                          <Box
                                            style={{
                                              marginLeft: "8px",
                                              fontSize: "12px",
                                              color: "gray",
                                            }}
                                          >
                                            {CheckActiveTerminal(row)}
                                          </Box>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <Box
                            style={{ marginTop: "20px", textAlign: "right" }}
                          >
                            {/* <SaveCard /> */}

                            <Box>
                              <Button
                                fullWidth
                                style={{
                                  minWidth: "200px",
                                  textTransform: "none",
                                }}
                                disabled={
                                  isProcessing ||
                                  terminal === "0" ||
                                  paymentProcessed
                                }
                                variant="contained"
                                onClick={(e) => ProcessPayment()}
                              >
                                {paymentProcessed ? (
                                  <>
                                    Payment{" "}
                                    {paymentData.status.indexOf("Fail") >= 0
                                      ? paymentData.status
                                      : "Processed"}{" "}
                                  </>
                                ) : (
                                  <>
                                    {!isProcessing && (
                                      <span>
                                        {tokenClaims.TransactionTypeId === "1"
                                          ? "Return "
                                          : "Pay "}
                                        {dollarUSLocale.format(
                                          paymentData.totalAmount,
                                        )}
                                      </span>
                                    )}
                                    {isProcessing && (
                                      <span>
                                        {paymentDone
                                          ? getTerminalFinalizingText()
                                          : `Waiting for the payment (${dollarUSLocale.format(
                                              paymentData.totalAmount,
                                            )})`}
                                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                        <CircularProgress
                                          size="1rem"
                                          style={{
                                            color:
                                              paymentDone === true
                                                ? "green"
                                                : "orange",
                                          }}
                                        ></CircularProgress>
                                      </span>
                                    )}
                                  </>
                                )}
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      className={classes.paymentBoxManual}
                      style={{ textAlign: "right" }}
                    >
                      <SaveCard />
                    </Box>
                  </Box>
                )}
                {tabValue === "Savecardterminal" && (
                  <Box>
                    <Box
                      className={classes.paymentBox}
                      style={{ minWidth: !isMobile() ? "400px" : "100px" }}
                    >
                      <Box style={{ border: "0px solid red" }}>
                        <Box style={{ marginTop: "15px" }}>
                          <FormControl size="small" fullWidth>
                            <InputLabel id="filter-type-label">
                              Payment Terminal
                            </InputLabel>
                            <Select
                              fullWidth
                              labelId="filter-type-label"
                              id="filter-type-select"
                              value={terminal}
                              onChange={(e) =>
                                setTerminal(e.target.value as string)
                              }
                              label="Payment Terminal 123"
                            >
                              <MenuItem value="0">Choose One</MenuItem>
                              {Terminals().map((row: any, index: number) => (
                                <MenuItem value={row.id} key={index}>
                                  <table>
                                    <tbody>
                                      <tr>
                                        <td
                                          style={{
                                            verticalAlign: "bottom",
                                          }}
                                        >
                                          {CheckActiveTermininalInMin(row) >
                                            60 && (
                                            <ClearOutlinedIcon
                                              style={{
                                                fontSize: "15",
                                                color: "red",
                                                marginRight: "10px",
                                              }}
                                            ></ClearOutlinedIcon>
                                          )}

                                          {CheckActiveTermininalInMin(row) <
                                            60 && (
                                            <CheckIcon
                                              style={{
                                                fontSize: "15",
                                                color:
                                                  CheckActiveTermininalInMin(
                                                    row,
                                                  ) < 10
                                                    ? "green"
                                                    : "orange",
                                                marginRight: "10px",
                                              }}
                                            ></CheckIcon>
                                          )}
                                        </td>
                                        <td>{row.terminalName}</td>
                                        <td>
                                          <Box
                                            style={{
                                              marginLeft: "8px",
                                              fontSize: "12px",
                                              color: "gray",
                                            }}
                                          >
                                            {CheckActiveTerminal(row)}
                                          </Box>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <Box
                            style={{
                              marginTop: "20px",
                              textAlign: "right",
                            }}
                          >
                            {/* <SaveCard /> */}

                            <Box>
                              <Button
                                fullWidth
                                style={{
                                  minWidth: "200px",
                                  textTransform: "none",
                                }}
                                disabled={
                                  isProcessing ||
                                  terminal === "0" ||
                                  paymentProcessed
                                }
                                variant="contained"
                                onClick={(e) => ProcessPayment(true)}
                              >
                                {paymentProcessed ? (
                                  <>
                                    Card{" "}
                                    {paymentData.status.indexOf("Fail") >= 0
                                      ? paymentData.status
                                      : "Saved"}{" "}
                                  </>
                                ) : (
                                  <>
                                    {!isProcessing && (
                                      <span>
                                        {tokenClaims.TransactionTypeId === "1"
                                          ? "Return "
                                          : "Save Card "}
                                      </span>
                                    )}
                                    {isProcessing && (
                                      <span>
                                        {paymentDone
                                          ? "Saving card"
                                          : `Waiting for the Card Details`}
                                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                        <CircularProgress
                                          size="1rem"
                                          style={{
                                            color:
                                              paymentDone === true
                                                ? "green"
                                                : "orange",
                                          }}
                                        ></CircularProgress>
                                      </span>
                                    )}
                                  </>
                                )}
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      className={classes.paymentBoxManual}
                      style={{ textAlign: "right" }}
                    ></Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
}
